import React from 'react';

import { runAggregate, type AnalyticsHost } from './api';
import { pageListQuery, viewsByPath, type Span } from './queries';
import { postTrafficQuality } from './quality/api';
import { facetRequest } from './quality/compile';
import { enqueueQuality } from './quality/queue';
import type { QualityFacetRequest, QualityPageFacet, QualityPageKind, QualityRange } from './quality/types';
import { viewsKey } from './useLandingPageViews';

/** How many of the website's pages the picker lists (the most viewed, or the best matches of a search). */
const SITE_PAGES = 20;
/** Typing settles this long before the website pages are asked for again. */
const SEARCH_SETTLE_MS = 250;

export type PageOption = { path: string; views: number };

export type PageOptions = {
  /** Views per landing page path in the period, keyed by `viewsKey` (a path with and without its trailing slash is one
   *  page); empty until read, or when the brand has no landing address. */
  landingViews: Map<string, number>;
  /** The website's pages for the current search, most viewed first. */
  sitePages: PageOption[];
  /** The website pages for the current search are being read. */
  loading: boolean;
  /** The website pages for the current search could not be read. */
  error: boolean;
};

/** The picker in quality mode: page facets over the report's effective addresses and range. */
export type PageOptionsQuality = {
  /** The report's `scope.selectedHosts` once it has settled; null before. An empty list asks nothing. */
  selectedHosts: readonly string[] | null;
  range: QualityRange;
  /** The report failed with no answer to take addresses from: the picker shows its failed state, not loading. */
  failed?: boolean;
};

/** Page discovery through the tenant's queue: the facet's rows, or none when the brand has no address. */
const readPages = async (
  tenantId: number,
  kind: QualityPageKind,
  body: QualityFacetRequest,
  signal: AbortSignal,
): Promise<{ path: string; pageViews: number }[]> => {
  const answer = await enqueueQuality(
    tenantId,
    `facet:page:${kind}`,
    () => postTrafficQuality(tenantId, body, signal),
    signal,
  );
  return 'facet' in answer && answer.facet.name === 'page' ? answer.facet.rows : [];
};

const hostsOf = (hosts: AnalyticsHost[], kind: AnalyticsHost['kind']): string[] =>
  hosts.filter((h) => h.kind === kind).map((h) => h.host);

/**
 * The page picker's lists, read only while it is open: the landing pages' views (to rank and label them) and the
 * website's pages, narrowed server-side by `search` (path contains, ignoring case). Each read gets an id from a
 * growing counter and only the newest lands; a failed read keeps the landing views it already had.
 */
/** No views known (nothing read yet for this brand and period). */
const NO_VIEWS: Map<string, number> = new Map();

export const usePageOptions = (
  tenantId: number,
  hosts: AnalyticsHost[],
  span: Span,
  open: boolean,
  search: string,
  quality?: PageOptionsQuality,
): PageOptions => {
  // The landing pages' views with the brand and period they were read for: shown only while those still match, so
  // another period's or brand's counts are never shown as this one's (a failed read shows none).
  const [landingHeld, setLandingHeld] = React.useState<{ key: string; views: Map<string, number> }>(() => ({
    key: '',
    views: new Map(),
  }));
  // Tagged with the mode and brand it was read for, so a mode flip or a brand switch never shows the other's pages
  // (a new search or period keeps the pages shown while it reads).
  const [site, setSite] = React.useState<{ readFor: string; pages: PageOption[]; loading: boolean; error: boolean }>({
    readFor: '',
    pages: [],
    loading: false,
    error: false,
  });
  const [query, setQuery] = React.useState(search.trim());
  const siteRead = React.useRef(0);

  // Quality mode never reads legacy counts: until its facets are read the picker has none (no count, never 0).
  const legacy = quality === undefined;
  const landingKey = legacy ? hostsOf(hosts, 'landing').join(',') : '';
  const siteKey = legacy ? hostsOf(hosts, 'site').join(',') : '';
  const { from, to } = span;
  const hostsAsked = quality?.selectedHosts;
  // The quality facets as asked (none while closed, before the report settled, or with no address).
  const qualityAsk = (facet: QualityPageFacet): string =>
    open && quality && hostsAsked?.length ? JSON.stringify(facetRequest(facet, quality.range, hostsAsked, {})) : '';
  const landingAsk = qualityAsk({ name: 'page', kind: 'landing' });
  const siteAsk = qualityAsk({ name: 'page', kind: 'site', search: query });
  const viewsFor = legacy ? `${tenantId}:${from}:${to}` : `quality:${tenantId}:${landingAsk}`;
  const siteFor = `${legacy ? 'legacy' : 'quality'}:${tenantId}`;
  const landingViews = landingHeld.key === viewsFor ? landingHeld.views : NO_VIEWS;

  // Typing settles before the website pages are asked for again.
  React.useEffect(() => {
    const id = window.setTimeout(() => setQuery(search.trim()), SEARCH_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, [search]);

  // Syncs with the analytics service: the landing pages' views, once per opening.
  React.useEffect(() => {
    if (!open || !landingKey) return undefined;
    const controller = new AbortController();
    void runAggregate(
      tenantId,
      { ...pageListQuery({ from, to }, '', 50), hosts: landingKey.split(',') },
      controller.signal,
    )
      .then((answer) => {
        if (controller.signal.aborted) return;
        // Keyed as the tab and the library cards count a page: both ways of opening it summed.
        const views = new Map<string, number>();
        viewsByPath(answer.rows).forEach((p) => {
          const k = viewsKey(p.path);
          views.set(k, (views.get(k) ?? 0) + p.views);
        });
        setLandingHeld({ key: `${tenantId}:${from}:${to}`, views });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [open, tenantId, landingKey, from, to]);

  // Syncs with the analytics service: the website's pages for the settled search.
  React.useEffect(() => {
    if (!open || !siteKey) return undefined;
    siteRead.current += 1;
    const id = siteRead.current;
    const controller = new AbortController();
    setSite((s) =>
      s.readFor === siteFor
        ? { ...s, loading: true, error: false }
        : { readFor: siteFor, pages: [], loading: true, error: false },
    );
    void runAggregate(
      tenantId,
      { ...pageListQuery({ from, to }, query, SITE_PAGES), hosts: siteKey.split(',') },
      controller.signal,
    )
      .then((answer) => {
        if (id === siteRead.current)
          setSite({ readFor: siteFor, pages: viewsByPath(answer.rows), loading: false, error: false });
      })
      .catch(() => {
        if (id === siteRead.current && !controller.signal.aborted)
          setSite({ readFor: siteFor, pages: [], loading: false, error: true });
      });
    return () => controller.abort();
  }, [open, tenantId, siteKey, from, to, query, siteFor]);

  // Syncs with the analytics service: the landing top list over the report's addresses, once per opening.
  React.useEffect(() => {
    if (!landingAsk) return undefined;
    const controller = new AbortController();
    void readPages(tenantId, 'landing', JSON.parse(landingAsk) as QualityFacetRequest, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        const views = new Map<string, number>();
        rows.forEach((p) => {
          const k = viewsKey(p.path);
          views.set(k, (views.get(k) ?? 0) + p.pageViews);
        });
        setLandingHeld({ key: `quality:${tenantId}:${landingAsk}`, views });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [tenantId, landingAsk]);

  // Syncs with the analytics service: the site pages for the settled search over the report's addresses.
  React.useEffect(() => {
    if (!siteAsk) return undefined;
    siteRead.current += 1;
    const id = siteRead.current;
    const controller = new AbortController();
    setSite((s) =>
      s.readFor === siteFor
        ? { ...s, loading: true, error: false }
        : { readFor: siteFor, pages: [], loading: true, error: false },
    );
    void readPages(tenantId, 'site', JSON.parse(siteAsk) as QualityFacetRequest, controller.signal)
      .then((rows) => {
        if (id === siteRead.current)
          setSite({
            readFor: siteFor,
            pages: rows.map((r) => ({ path: r.path, views: r.pageViews })),
            loading: false,
            error: false,
          });
      })
      .catch(() => {
        if (id === siteRead.current && !controller.signal.aborted)
          setSite({ readFor: siteFor, pages: [], loading: false, error: true });
      });
    return () => controller.abort();
  }, [tenantId, siteAsk, siteFor]);

  // Before the report settles the picker waits for its addresses (a failed report has none to wait for); with none
  // chosen it asks nothing.
  if (!legacy && !hostsAsked)
    return { landingViews: NO_VIEWS, sitePages: [], loading: !quality?.failed, error: !!quality?.failed };
  if (!legacy && !hostsAsked?.length) return { landingViews: NO_VIEWS, sitePages: [], loading: false, error: false };
  // Until this mode's and brand's read starts, the pages of the one before are not shown.
  if (site.readFor !== siteFor)
    return { landingViews, sitePages: [], loading: Boolean(legacy ? open && siteKey : siteAsk), error: false };
  return { landingViews, sitePages: site.pages, loading: site.loading, error: site.error };
};
